import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Disc3, MailCheck, ArrowLeft } from "lucide-react";
import { useAuth } from "../auth";
import { useLang } from "../i18n";
import api, { formatApiError } from "../api";

const VerifyEmail = () => {
  const { t } = useLang();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (user === false) navigate("/accedi?return=/verifica-email", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const iv = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(iv);
  }, [cooldown]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-email", { code });
      setUser(data);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err, t("verify_wrong")));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setError(""); setNotice("");
    try {
      await api.post("/auth/verify-email/resend");
      setNotice(t("verify_resent"));
      setCooldown(60);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const field = "w-full rounded-lg border border-white/15 bg-black/40 px-4 py-3 text-center text-2xl font-mono2 tracking-[0.5em] placeholder:text-mute focus:border-ember focus:outline-none transition-colors";

  return (
    <main className="pt-16 min-h-screen flex items-center justify-center px-5" data-testid="verify-email-page">
      <div className="orb absolute top-10 left-10 w-96 h-96 rounded-full bg-ember/15" />
      <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-card p-8 sm:p-10">
        <Link to="/dashboard" className="inline-flex items-center gap-2 font-mono2 text-[10px] tracking-[0.25em] uppercase text-mute hover:text-ember transition-colors mb-8">
          <ArrowLeft className="w-3.5 h-3.5" /> {t("story_back")}
        </Link>
        <div className="flex items-center gap-3 mb-2">
          {user?.email_verified ? <MailCheck className="w-6 h-6 text-gold" /> : <Disc3 className="w-6 h-6 text-ember" />}
          <h1 className="font-display font-bold text-2xl" data-testid="verify-title">{t("verify_title")}</h1>
        </div>
        {user?.email_verified ? (
          <div data-testid="verify-done">
            <p className="text-gold text-sm mt-4 mb-8">{t("verify_done")}</p>
            <Link to="/dashboard" data-testid="verify-goto-dashboard"
              className="block text-center w-full rounded-full bg-ember py-3.5 text-sm font-semibold uppercase tracking-wider text-black hover:bg-gold transition-colors">
              {t("verify_continue")}
            </Link>
          </div>
        ) : (
          <>
            <p className="text-mute text-sm mt-3 mb-8" data-testid="verify-sub">
              {t("verify_sub")} <span className="text-mist">{user?.email}</span>
            </p>
            <form onSubmit={submit} className="space-y-4">
              <input data-testid="verify-code-input" required inputMode="numeric" maxLength={6}
                className={field} placeholder="——————"
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} />
              {error && <p data-testid="verify-error" className="text-sm text-red-400">{error}</p>}
              {notice && <p data-testid="verify-notice" className="text-sm text-gold">{notice}</p>}
              <button data-testid="verify-submit-button" disabled={busy || code.length !== 6}
                className="w-full rounded-full bg-ember py-3.5 text-sm font-semibold uppercase tracking-wider text-black hover:bg-gold transition-colors disabled:opacity-50">
                {t("verify_btn")}
              </button>
            </form>
            <button onClick={resend} disabled={cooldown > 0} data-testid="verify-resend-button"
              className="mt-6 w-full text-center font-mono2 text-[11px] tracking-[0.2em] uppercase text-mute hover:text-ember transition-colors disabled:opacity-40">
              {cooldown > 0 ? `${t("verify_resend")} (${cooldown}s)` : t("verify_resend")}
            </button>
          </>
        )}
      </div>
    </main>
  );
};

export default VerifyEmail;
